import { State, Action, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { PrintersService } from './printers.service';
import { PrintersResponse, PrintersStateModel, PrinterTypesApiResponse } from './printers.model';
import {
  LoadPrinters,
  AddPrinter,
  DeletePrinter,
  LoadPrinterTypes,
  UpdatePrinter,
  LoadAllPrinters,
} from './printers.actions';
import { catchError, tap } from 'rxjs';
import { PaginationParams } from '../general-store/general.model';
import { NotificationService } from '../../app/services/notification.service';
import { Printer } from './printers.model';

@State<PrintersStateModel>({
  name: 'printers',
  defaults: {
    printers: [],
    allPrinters: [],
    printerTypes: null,
    pagination: {
      totalItems: 0,
      totalPages: 0,
      currentPage: 0,
      itemsPerPage: 0,
    },
    headers: [],
    printersListLoading: false,
    printersLoading: false,
    currentParams: null,
  },
})
@Injectable({ providedIn: 'root' })
export class PrintersState {
  private readonly printersService = inject(PrintersService);
  private readonly notificationService = inject(NotificationService);

  private paramsMatch(
    params1: PaginationParams | null | undefined,
    params2: PaginationParams | null | undefined,
  ): boolean {
    if (!params1 && !params2) return true;
    if (!params1 || !params2) return false;
    return (
      params1.first === params2.first &&
      params1.rows === params2.rows &&
      params1.sortBy === params2.sortBy &&
      params1.sortOrder === params2.sortOrder &&
      params1.search === params2.search &&
      params1.filters === params2.filters
    );
  }

  @Action(LoadPrinters)
  loadPrinters(ctx: StateContext<PrintersStateModel>, action: LoadPrinters) {
    const state = ctx.getState();
    const requestedParams = action.payload || {
      first: 0,
      rows: 5,
      sortBy: 'createdAt',
      sortOrder: 'DESC',
      search: '',
      filters: '',
    };

    // Check if we already have data for these exact params
    if (
      this.paramsMatch(state.currentParams, requestedParams) &&
      state.printers.length > 0 &&
      !state.printersListLoading
    ) {
      // Data already loaded for these params, no need to fetch
      return;
    }

    ctx.patchState({ printersListLoading: true });
    return this.printersService.listPrinters(requestedParams).pipe(
      tap((response: PrintersResponse) => {
        ctx.patchState({
          printers: response.payload ?? [],
          pagination: response.pagination ?? {
            totalItems: 0,
            totalPages: 0,
            currentPage: 0,
            itemsPerPage: 0,
          },
          headers: response.headers ?? [],
          printersListLoading: false,
          currentParams: requestedParams,
        });
      }),
    );
  }

  @Action(LoadAllPrinters)
  loadAllPrinters(ctx: StateContext<PrintersStateModel>) {
    const state = ctx.getState();

    // Check if we already have all printers cached
    if (state.allPrinters.length > 0) {
      return; // Already loaded, use cache
    }

    ctx.patchState({ printersLoading: true });
    return this.printersService
      .listPrinters({
        first: 0,
        rows: 10000, // Large limit to get all printers
        sortBy: 'createdAt',
        sortOrder: 'DESC',
        search: '',
        filters: '',
      })
      .pipe(
        tap((response: PrintersResponse) => {
          ctx.patchState({
            allPrinters: response.payload ?? [],
            printersLoading: false,
          });
        }),
      );
  }

  @Action(LoadPrinterTypes)
  loadPrinterTypes(ctx: StateContext<PrintersStateModel>) {
    ctx.patchState({ printersLoading: true });
    return this.printersService.getPrinterTypes().pipe(
      tap((response: PrinterTypesApiResponse) => {
        ctx.patchState({
          printerTypes: response.payload ?? null,
          printersLoading: false,
        });
      }),
    );
  }

  @Action(AddPrinter)
  addPrinter(ctx: StateContext<PrintersStateModel>, action: AddPrinter) {
    ctx.patchState({ printersLoading: true });
    const state = ctx.getState();
    return this.printersService.addPrinter(action.payload).pipe(
      tap((response) => {
        this.notificationService.openSuccessToast('Printer added successfully');

        // Patch state: add new printer and update pagination
        const updatedPrinters = [response.payload, ...state.printers];
        const updatedPagination = {
          ...state.pagination,
          totalItems: state.pagination.totalItems + 1,
        };
        const updatedAllPrinters = [...state.allPrinters, response.payload];

        ctx.patchState({
          printers: updatedPrinters,
          pagination: updatedPagination,
          allPrinters: updatedAllPrinters,
          printersLoading: false,
        });
      }),
    );
  }

  @Action(UpdatePrinter)
  updatePrinter(ctx: StateContext<PrintersStateModel>, action: UpdatePrinter) {
    ctx.patchState({ printersLoading: true });

    return this.printersService.updatePrinter(action.id, action.payload).pipe(
      tap((response: any) => {
        const updatedPrinter: Printer = response.payload;
        const state = ctx.getState();

        const normalize = (printers: Printer[]) =>
          printers.map((p) => {
            if (p.id === updatedPrinter.id) {
              return updatedPrinter;
            }

            // If updated printer is default, unmark others
            if (updatedPrinter.isDefault) {
              return { ...p, isDefault: false };
            }

            return p;
          });

        ctx.patchState({
          printers: normalize(state.printers),
          allPrinters: normalize(state.allPrinters),
          printersLoading: false,
        });

        this.notificationService.openSuccessToast('Printer updated successfully');
      }),
      catchError((err) => {
        ctx.patchState({ printersLoading: false });
        throw err;
      }),
    );
  }

  @Action(DeletePrinter)
  deletePrinter(ctx: StateContext<PrintersStateModel>, action: DeletePrinter) {
    ctx.patchState({ printersLoading: true });
    const state = ctx.getState();
    return this.printersService.deletePrinter(action.id).pipe(
      tap(() => {
        // Patch state: remove the printer from the array and update pagination
        const updatedPrinters = state.printers.filter((printer) => printer.id !== action.id);
        const updatedAllPrinters = state.allPrinters.filter((printer) => printer.id !== action.id);
        const updatedPagination = {
          ...state.pagination,
          totalItems: Math.max(0, state.pagination.totalItems - 1),
        };

        ctx.patchState({
          printers: updatedPrinters,
          pagination: updatedPagination,
          allPrinters: updatedAllPrinters,
          printersLoading: false,
        });
      }),
    );
  }
}
